import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Cancel,
  NotificationsActive,
  Close,
  Refresh
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { videoService, storyService } from '../../services/api';
import SendNotificationModal from '../notifications/SendNotificationModal';

const ViewersModal = ({ open, onClose, contentType, contentId, contentTitle }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({ viewers: [], summary: null, total: 0, totalPages: 1 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [reminderModal, setReminderModal] = useState({ open: false, viewer: null });

  const fetchViewers = useCallback(async () => {
    if (!open || !contentId) return;
    try {
      setLoading(true);
      setError('');
      const params = {
        status: statusFilter,
        search,
        page: page + 1,
        limit: rowsPerPage
      };
      const service = contentType === 'video' ? videoService : storyService;
      const response = await service.getViewers(contentId, params);
      setData({
        viewers: response.viewers || [],
        summary: response.summary || null,
        total: response.total || 0,
        totalPages: response.totalPages || 1
      });
    } catch (err) {
      console.error('Error fetching viewers:', err);
      setError(err.response?.data?.error || t('viewers.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [open, contentId, contentType, statusFilter, search, page, rowsPerPage, t]);

  // Reset state when reopening for a different content item
  useEffect(() => {
    if (open) {
      setStatusFilter('all');
      setSearch('');
      setPage(0);
      setRowsPerPage(25);
    }
  }, [open, contentId]);

  // Debounced search + immediate fetch on filter change
  useEffect(() => {
    if (!open) return;
    const timeoutId = setTimeout(fetchViewers, search ? 500 : 0);
    return () => clearTimeout(timeoutId);
  }, [fetchViewers, open, search]);

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleChangePage = (_event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const formatDateTime = (date) =>
    date ? new Date(date).toLocaleString() : '-';

  const fullName = (customer) => {
    const parts = [customer?.firstName, customer?.lastName].filter(Boolean);
    return parts.join(' ') || customer?.email || '-';
  };

  const summaryChip = (label, value, color) => (
    <Chip label={`${label}: ${value}`} color={color} size="small" sx={{ mr: 1 }} />
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {contentType === 'video'
            ? t('viewers.modalTitleVideo')
            : t('viewers.modalTitleStory')}
          {contentTitle && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
              {contentTitle}
            </Typography>
          )}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {data.summary && (
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {summaryChip(t('viewers.total'), data.summary.total, 'default')}
              {summaryChip(t('viewers.watchedCount'), data.summary.watchedCount, 'success')}
              {summaryChip(t('viewers.notWatchedCount'), data.summary.notWatchedCount, 'warning')}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t('viewers.statusFilter')}</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusChange}
                label={t('viewers.statusFilter')}
              >
                <MenuItem value="all">{t('viewers.allViewers')}</MenuItem>
                <MenuItem value="watched">{t('viewers.watched')}</MenuItem>
                <MenuItem value="not-watched">{t('viewers.notWatched')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              variant="outlined"
              placeholder={t('viewers.searchPlaceholder')}
              value={search}
              onChange={handleSearchChange}
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={t('common.refresh') || 'Refresh'}>
              <IconButton onClick={fetchViewers} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('viewers.fullName')}</TableCell>
                  <TableCell>{t('viewers.email')}</TableCell>
                  <TableCell>{t('viewers.status')}</TableCell>
                  <TableCell>{t('viewers.lastViewedAt')}</TableCell>
                  {contentType === 'video' && (
                    <TableCell>{t('viewers.watchPercentage')}</TableCell>
                  )}
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={contentType === 'video' ? 6 : 5} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : data.viewers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={contentType === 'video' ? 6 : 5} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">{t('viewers.noResults')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.viewers.map((row) => (
                    <TableRow key={row.customer._id} hover>
                      <TableCell>{fullName(row.customer)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {row.customer.email || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.watched ? (
                          <Chip
                            icon={<CheckCircle />}
                            label={t('viewers.watched')}
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<Cancel />}
                            label={t('viewers.notWatched')}
                            color="warning"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.watched ? formatDateTime(row.lastViewedAt) : '-'}
                        </Typography>
                      </TableCell>
                      {contentType === 'video' && (
                        <TableCell>
                          {row.watched ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">
                                {Math.round(row.watchPercentage || 0)}%
                              </Typography>
                              {row.completed && (
                                <Chip label={t('viewers.completed')} color="success" size="small" variant="outlined" />
                              )}
                            </Box>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {!row.watched && (
                          <Tooltip title={t('viewers.sendReminder')}>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => setReminderModal({ open: true, viewer: row })}
                            >
                              <NotificationsActive fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={data.total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={t('common.rowsPerPage')}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {reminderModal.viewer && (
        <SendNotificationModal
          open={reminderModal.open}
          onClose={() => {
            setReminderModal({ open: false, viewer: null });
            fetchViewers();
          }}
          contentType={contentType}
          contentId={contentId}
          prefilledTargetUserId={reminderModal.viewer.customer._id}
          prefilledTargetUserLabel={`${fullName(reminderModal.viewer.customer)}${
            reminderModal.viewer.customer.email ? ` (${reminderModal.viewer.customer.email})` : ''
          }`}
        />
      )}
    </>
  );
};

export default ViewersModal;
